/**
 * ContraForm.jsx — v2
 * FIXES:
 *   - Full edit for contra entries (inline edit row)
 *   - Auto-select single bank in From/To dropdowns
 *   - Reverse chronological order (latest first)
 *   - Default date = last contra transaction date from localStorage
 *   - Month auto-derives from selected date
 */
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Plus, ArrowRight, Info, Trash2, Pencil, X, Check } from 'lucide-react';
import { createContra, updateTransaction, getTransactions, deleteTransaction } from '../../api/yea.api';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import '../accounting/yea.css';

const MONTHS = ['April','May','June','July','August','September','October','November','December','January','February','March'];
const CONTRA_TYPES = [
  { value: 'cash_to_bank', label: 'Cash → Bank', desc: 'Deposit cash into a bank account' },
  { value: 'bank_to_cash', label: 'Bank → Cash', desc: 'Withdraw cash from bank' },
  { value: 'bank_to_bank', label: 'Bank → Bank', desc: 'Transfer between two banks' },
];

// Fix 6 — local date string to avoid UTC off-by-one (31 Mar unselectable)
const toLocalDateStr = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const LS_KEY_CONTRA = 'drusshti_last_date_contra';

// Derive FY month name from a date string
const monthFromDate = (dateStr) => {
  if (!dateStr) return MONTHS[new Date().getMonth() >= 3 ? new Date().getMonth() - 3 : new Date().getMonth() + 9];
  const d = new Date(dateStr);
  const jsMonth = d.getMonth();
  const fyIndex = jsMonth >= 3 ? jsMonth - 3 : jsMonth + 9;
  return MONTHS[fyIndex];
};

const getDefaultDate = () => {
  return localStorage.getItem(LS_KEY_CONTRA) || new Date().toISOString().slice(0, 10);
};

const emptyForm = (banks) => {
  const defaultDate = getDefaultDate();
  // Auto-select single bank
  const singleBank = banks?.length === 1 ? banks[0] : null;
  return {
    contraType:        'cash_to_bank',
    date:              defaultDate,
    month:             monthFromDate(defaultDate),
    totalAmount:       '',
    narration:         '',
    contraFromBankId:  singleBank ? singleBank._id : '',
    contraFromBankName: singleBank ? singleBank.bankName : '',
    contraToBankId:    singleBank ? singleBank._id : '',
    contraToBankName:  singleBank ? singleBank.bankName : '',
  };
};

const ContraForm = ({ societyId, fyId, banks }) => {
  const [contras,    setContras]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form,       setForm]       = useState(() => emptyForm(banks));

  // Edit state
  const [editId,     setEditId]     = useState(null);
  const [editForm,   setEditForm]   = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const fetchContras = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getTransactions(societyId, fyId, { type: 'contra' });
      // Reverse chronological — latest first
      const sorted = [...(r.data.transactions || [])].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      setContras(sorted);

      // Store latest contra date for next Add
      if (sorted.length > 0) {
        const latestDate = new Date(sorted[0].date).toISOString().slice(0, 10);
        localStorage.setItem(LS_KEY_CONTRA, latestDate);
      }
    } catch { toast.error('Failed to load contra entries'); }
    finally { setLoading(false); }
  }, [societyId, fyId]);

  useEffect(() => { fetchContras(); }, [fetchContras]);

  // When banks load, apply auto-select to form if empty
  useEffect(() => {
    if (banks?.length === 1) {
      setForm(f => ({
        ...f,
        contraFromBankId:   f.contraFromBankId   || banks[0]._id,
        contraFromBankName: f.contraFromBankName || banks[0].bankName,
        contraToBankId:     f.contraToBankId     || banks[0]._id,
        contraToBankName:   f.contraToBankName   || banks[0].bankName,
      }));
    }
  }, [banks]);

  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleDateChange = (val) => {
    setForm(f => ({ ...f, date: val, month: monthFromDate(val) }));
  };

  const setBankField = (idF, nameF) => e => {
    const found = (banks || []).find(b => b._id === e.target.value);
    setForm(p => ({ ...p, [idF]: e.target.value, [nameF]: found?.bankName || '' }));
  };

  // ── Submit new contra ──────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.totalAmount || Number(form.totalAmount) <= 0) { toast.error('Amount required'); return; }
    if (form.contraType === 'bank_to_bank' && form.contraFromBankId === form.contraToBankId) {
      toast.error('Source and destination bank must differ'); return;
    }
    if (form.contraType !== 'cash_to_bank' && !form.contraFromBankId) { toast.error('Select From Bank'); return; }
    if (form.contraType !== 'bank_to_cash' && !form.contraToBankId)   { toast.error('Select To Bank');   return; }

    setSubmitting(true);
    try {
      await createContra(societyId, fyId, { ...form, totalAmount: Number(form.totalAmount) });
      localStorage.setItem(LS_KEY_CONTRA, form.date);
      toast.success('Contra entry saved');
      setForm(emptyForm(banks));
      fetchContras();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  // ── Start editing a contra row ─────────────────────────────────────────────
  const startEdit = (c) => {
    setEditId(c._id);
    setEditForm({
      date:              new Date(c.date).toISOString().slice(0, 10),
      month:             c.month || monthFromDate(new Date(c.date).toISOString().slice(0, 10)),
      totalAmount:       c.totalAmount,
      narration:         c.narration || '',
      contraType:        c.contraType,
      contraFromBankId:  c.contraFromBankId  || '',
      contraFromBankName: c.contraFromBankName || '',
      contraToBankId:    c.contraToBankId    || '',
      contraToBankName:  c.contraToBankName  || '',
    });
  };

  const cancelEdit = () => { setEditId(null); setEditForm(null); };

  const sef  = k => e => setEditForm(p => ({ ...p, [k]: e.target.value }));
  const sefDate = val => setEditForm(p => ({ ...p, date: val, month: monthFromDate(val) }));
  const setEditBank = (idF, nameF) => e => {
    const found = (banks || []).find(b => b._id === e.target.value);
    setEditForm(p => ({ ...p, [idF]: e.target.value, [nameF]: found?.bankName || '' }));
  };

  // ── Save edited contra ─────────────────────────────────────────────────────
  const saveEdit = async () => {
    if (!editForm.totalAmount || Number(editForm.totalAmount) <= 0) { toast.error('Amount required'); return; }
    if (editForm.contraType === 'bank_to_bank' && editForm.contraFromBankId === editForm.contraToBankId) {
      toast.error('Source and destination bank must differ'); return;
    }
    setEditSaving(true);
    try {
      await updateTransaction(societyId, fyId, editId, {
        date:              editForm.date,
        month:             editForm.month,
        totalAmount:       Number(editForm.totalAmount),
        narration:         editForm.narration,
        contraType:        editForm.contraType,
        contraFromBankId:  editForm.contraFromBankId  || undefined,
        contraFromBankName: editForm.contraFromBankName || '',
        contraToBankId:    editForm.contraToBankId    || undefined,
        contraToBankName:  editForm.contraToBankName  || '',
        cashAmount: editForm.contraType === 'bank_to_cash' ? Number(editForm.totalAmount) : 0,
        bankAmount: editForm.contraType !== 'bank_to_cash' ? Number(editForm.totalAmount) : 0,
      });
      toast.success('Contra entry updated');
      cancelEdit();
      fetchContras();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setEditSaving(false); }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (txn) => {
    if (!window.confirm('Delete this contra entry?')) return;
    try { await deleteTransaction(societyId, fyId, txn._id); toast.success('Deleted'); fetchContras(); }
    catch { toast.error('Failed'); }
  };

  // ── Bank selector helper (with auto-select for single bank) ────────────────
  const BankSelect = ({ value, onChange, exclude }) => (
    <select className="field-input" value={value} onChange={onChange}>
      <option value="">{banks?.length === 0 ? 'No banks' : 'Select bank'}</option>
      {(banks || []).filter(b => !exclude || b._id !== exclude).map(b => (
        <option key={b._id} value={b._id}>{b.bankName}</option>
      ))}
    </select>
  );

  return (
    <div className="animate-in">
      <h2 style={{ marginBottom: '0.5rem' }}>Contra Entries</h2>
      <p className="text-sm text-muted" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <Info size={13} /> Contra entries affect balances but do not appear in Receipt &amp; Payment
      </p>

      {/* ── New Contra Form ── */}
      <div className="card" style={{ marginBottom: '1.75rem' }}>
        <h4 style={{ marginBottom: '1.25rem' }}>New Contra Entry</h4>
        <form onSubmit={handleSubmit} className="txn-form">

          {/* Contra type picker */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            {CONTRA_TYPES.map(ct => (
              <div key={ct.value} onClick={() => setForm(f => ({ ...f, contraType: ct.value }))}
                style={{
                  padding: '0.85rem', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  border: `1px solid ${form.contraType === ct.value ? 'var(--gold)' : 'var(--border-subtle)'}`,
                  background: form.contraType === ct.value ? 'var(--gold-glow)' : 'var(--bg-raised)',
                  transition: 'all var(--t-fast)'
                }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.2rem', color: form.contraType === ct.value ? 'var(--gold)' : 'var(--text)' }}>{ct.label}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{ct.desc}</div>
              </div>
            ))}
          </div>

          {/* Date + Month */}
          <div className="txn-form-grid">
            <Input label="Date" type="date" required value={form.date}
              onChange={e => handleDateChange(e.target.value)} />
            <div className="field">
              <label className="field-label">Month <span className="required">*</span></label>
              <div className="field-control">
                <select className="field-input" value={form.month} onChange={sf('month')}>
                  {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* From → To */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            {form.contraType !== 'cash_to_bank' ? (
              <div className="field" style={{ flex: 1 }}>
                <label className="field-label">From Bank</label>
                <div className="field-control">
                  <BankSelect value={form.contraFromBankId}
                    onChange={setBankField('contraFromBankId', 'contraFromBankName')}
                    exclude={form.contraType === 'bank_to_bank' ? form.contraToBankId : null} />
                </div>
              </div>
            ) : (
              <div className="field" style={{ flex: 1 }}>
                <label className="field-label">From</label>
                <div style={{ padding: '0.62rem 0.9rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Cash</div>
              </div>
            )}
            <ArrowRight size={18} color="var(--text-dim)" style={{ marginTop: '1.5rem', flexShrink: 0 }} />
            {form.contraType !== 'bank_to_cash' ? (
              <div className="field" style={{ flex: 1 }}>
                <label className="field-label">To Bank</label>
                <div className="field-control">
                  <BankSelect value={form.contraToBankId}
                    onChange={setBankField('contraToBankId', 'contraToBankName')}
                    exclude={form.contraType === 'bank_to_bank' ? form.contraFromBankId : null} />
                </div>
              </div>
            ) : (
              <div className="field" style={{ flex: 1 }}>
                <label className="field-label">To</label>
                <div style={{ padding: '0.62rem 0.9rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Cash</div>
              </div>
            )}
          </div>

          <div className="txn-form-grid">
            <Input label="Amount (₹)" type="number" min="0.01" step="0.01" required placeholder="0.00"
              value={form.totalAmount} onChange={sf('totalAmount')} />
            <Input label="Narration" placeholder="Optional" value={form.narration} onChange={sf('narration')} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" loading={submitting} icon={Plus}>Save Contra Entry</Button>
          </div>
        </form>
      </div>

      {/* ── Contra List ── */}
      {loading
        ? <div className="loading-center"><div className="spinner" /></div>
        : contras.length === 0
          ? <div className="empty-state"><p>No contra entries yet</p></div>
          : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="ledger-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Month</th>
                    <th>Voucher</th>
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Narration</th>
                    <th className="amount-cell">Amount (₹)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {contras.map(c => (
                    editId === c._id ? (
                      /* ── Inline Edit Row ── */
                      <tr key={c._id} style={{ background: 'var(--bg-overlay)' }}>
                        <td>
                          <input type="date" className="field-input" style={{ fontSize: '0.78rem', padding: '4px 6px' }}
                            value={editForm.date} onChange={e => sefDate(e.target.value)} />
                        </td>
                        <td>
                          <select className="field-input" style={{ fontSize: '0.78rem', padding: '4px 6px' }}
                            value={editForm.month} onChange={sef('month')}>
                            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                          </select>
                        </td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--info)', fontFamily: 'monospace' }}>{c.voucherNo}</td>
                        <td>
                          <select className="field-input" style={{ fontSize: '0.78rem', padding: '4px 6px' }}
                            value={editForm.contraType} onChange={sef('contraType')}>
                            {CONTRA_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                          </select>
                        </td>
                        <td>
                          {editForm.contraType !== 'cash_to_bank' ? (
                            <select className="field-input" style={{ fontSize: '0.78rem', padding: '4px 6px' }}
                              value={editForm.contraFromBankId} onChange={setEditBank('contraFromBankId', 'contraFromBankName')}>
                              <option value="">Select</option>
                              {(banks || []).map(b => <option key={b._id} value={b._id}>{b.bankName}</option>)}
                            </select>
                          ) : <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Cash</span>}
                        </td>
                        <td>
                          {editForm.contraType !== 'bank_to_cash' ? (
                            <select className="field-input" style={{ fontSize: '0.78rem', padding: '4px 6px' }}
                              value={editForm.contraToBankId} onChange={setEditBank('contraToBankId', 'contraToBankName')}>
                              <option value="">Select</option>
                              {(banks || []).map(b => <option key={b._id} value={b._id}>{b.bankName}</option>)}
                            </select>
                          ) : <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Cash</span>}
                        </td>
                        <td>
                          <input type="text" className="field-input" style={{ fontSize: '0.78rem', padding: '4px 6px' }}
                            value={editForm.narration} onChange={sef('narration')} placeholder="Narration" />
                        </td>
                        <td className="amount-cell">
                          <input type="number" className="field-input" style={{ fontSize: '0.82rem', padding: '4px 6px', textAlign: 'right', width: 100 }}
                            value={editForm.totalAmount} onChange={sef('totalAmount')} min="0.01" step="0.01" />
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.3rem' }}>
                            <Button size="xs" variant="ghost" icon={Check} loading={editSaving} onClick={saveEdit} />
                            <Button size="xs" variant="subtle" icon={X} onClick={cancelEdit} />
                          </div>
                        </td>
                      </tr>
                    ) : (
                      /* ── Normal display row ── */
                      <tr key={c._id}>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                          {new Date(c.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                        <td><span className="badge badge-subtle">{c.month}</span></td>
                        <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'var(--info)' }}>{c.voucherNo}</td>
                        <td><span className="badge badge-info">{CONTRA_TYPES.find(ct => ct.value === c.contraType)?.label || c.contraType}</span></td>
                        <td style={{ fontSize: '0.85rem' }}>{c.contraFromBankName || 'Cash'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{c.contraToBankName || 'Cash'}</td>
                        <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{c.narration || '—'}</td>
                        <td className="amount-cell" style={{ fontWeight: 600 }}>{Number(c.totalAmount).toLocaleString('en-IN')}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.3rem' }}>
                            <Button size="xs" variant="ghost" icon={Pencil} onClick={() => startEdit(c)} />
                            <Button size="xs" variant="danger" icon={Trash2} onClick={() => handleDelete(c)} />
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )
      }
    </div>
  );
};

export default ContraForm;
